import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../app/store'
import { IArticleTransformed } from '../article/types/IArticleTransformed'

export interface QueueState {
    queuedArticles: IArticleTransformed[]
    suggestedArticles: IArticleTransformed[]
    articleToPlayAfterQueue?: IArticleTransformed
}

const initialState: QueueState = {
    queuedArticles: [],
    suggestedArticles: [],

    // when all the queued articles are played then this will be played
    // this will be updated before playing a queued article
    // this must be one of the articles fetched from the backend initially(when app loads for the first time) and not from the searched articles
    articleToPlayAfterQueue: undefined,
}

export const queueSlice = createSlice({
    name: 'queue',
    initialState,
    reducers: {
        pushQueue: (
            state,
            action: PayloadAction<{ currentlyPlayingArticle?: IArticleTransformed; article: IArticleTransformed }>
        ) => {
            const {
                payload: { article, currentlyPlayingArticle },
            } = action

            // the reason for this conditional check is that if the currentlyPlayingArticle has the queueId and there is not article present in the queue then the queueId for the nextArticle should be currentlyPlayingArticle.queueId + 1 otherwise the currentlyPlayingArticle and the new article(to be queued) could have the same queueId which causes problems
            let queueId = (state.queuedArticles?.[state.queuedArticles.length - 1]?.queueId || 0) + 1
            if (queueId === currentlyPlayingArticle?.queueId) {
                queueId += 1
            }

            state.queuedArticles = [...state.queuedArticles, { ...article, queueId }]

            if (state.suggestedArticles.some(suggestedArticle => article.id === suggestedArticle.id)) {
                state.suggestedArticles = [
                    ...state.suggestedArticles.filter(suggestedArticle => article.id !== suggestedArticle.id),
                ]
            }
        },

        popQueuedArticleById: (state, action: PayloadAction<IArticleTransformed['queueId']>) => {
            state.queuedArticles = [...state.queuedArticles.filter(article => article.queueId !== action.payload)]
        },

        popSuggestedArticleById: (state, action: PayloadAction<IArticleTransformed['queueId']>) => {
            state.suggestedArticles = [
                ...state.suggestedArticles.filter(suggestedArticle => action.payload !== suggestedArticle.queueId),
            ]
        },

        setSuggestedArticles: (state, action: PayloadAction<IArticleTransformed[]>) => {
            state.suggestedArticles = action.payload.map((article, index) => ({
                ...article,
                queueId: Number(article.id.toString() + (index + 1).toString() + article.id.toString()),
            }))
        },

        setArticleToPlayAfterQueue: (state, action: PayloadAction<IArticleTransformed>) => {
            state.articleToPlayAfterQueue = { ...action.payload }
        },
        resetQueueStorage: state => {
            state.queuedArticles = []
        },
    },
})

export const {
    pushQueue,
    popQueuedArticleById,
    setSuggestedArticles,
    popSuggestedArticleById,
    setArticleToPlayAfterQueue,
    resetQueueStorage,
} = queueSlice.actions
export const selectQueuedArticles = (state: RootState) => state.queue.queuedArticles
export const selectSuggestedArticles = (state: RootState) => state.queue.suggestedArticles
export const selectArticleToPlayAfterQueue = (state: RootState) => state.queue.articleToPlayAfterQueue

export default queueSlice.reducer
